import React, { useState } from 'react';
import { Popover } from '@headlessui/react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  getDay,
  isSameDay,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import Calendar from 'svg/Calendar';
import ChevronLeft from 'svg/ChevronLeft';
import ChevronRight from 'svg/ChevronRight';
import ChevronUp from 'svg/ChevronUp';
import Button from 'components/Button';
import classNames from 'styles/utils/classNames';
import { ClinicDatePickerProps } from '../props';

const ClinicDatePicker: React.FC<ClinicDatePickerProps> = ({
  selectedDate,
  onDateChange,
  wrapperClassName,
}) => {
  const [currentDate, setCurrentDate] = useState(selectedDate || new Date());

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const nextDay = new Date(today);
  nextDay.setDate(today.getDate() + 1);
  nextDay.setHours(0, 0, 0, 0);

  const startOfCurrentMonth = startOfMonth(currentDate);
  const endOfCurrentMonth = endOfMonth(currentDate);
  const startOfWeekForCurrentMonth = startOfWeek(startOfCurrentMonth);
  const endOfWeekForCurrentMonth = endOfWeek(endOfCurrentMonth);

  const days = eachDayOfInterval({
    start: startOfWeekForCurrentMonth,
    end: endOfWeekForCurrentMonth,
  });

  const handlePrevMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const handleDateSelect = (date: Date, closePopover: () => void) => {
    if (isDaySelectable(date)) {
      onDateChange(date);
      closePopover();
    }
  };

  const isDaySelectable = (day: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const compareDay = new Date(day);
    compareDay.setHours(0, 0, 0, 0);

    return compareDay >= today;
  };

  return (
    <Popover className={classNames('relative shrink-0', wrapperClassName)}>
      {({ open, close }) => (
        <>
          <Popover.Button className="typography-product-button-label-medium flex w-full items-center justify-between gap-2 rounded-md bg-color-bg-input-lightmode-primary  px-4 py-3 dark:bg-color-bg-darkmode-primary md:rounded-full">
            <div className="flex items-center gap-2">
              <Calendar className="mb-[0.5px] h-5 w-5 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
              <span className="max-sm:typography-product-button-label-small text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                {selectedDate ? format(selectedDate, 'MMM d') : 'Select date'}
              </span>
            </div>
            <ChevronUp
              className={classNames(
                'h-4 w-4 shrink-0 rotate-180 transition-transform',
                open && '!rotate-0',
              )}
            />
          </Popover.Button>

          <Popover.Panel className="absolute right-0 z-10 mt-2 w-full rounded-3xl border border-color-border-input-lightmode bg-color-bg-lightmode-primary px-5 py-6 shadow-[0px_4px_20px_0px_rgba(0,0,0,0.16)] dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary md:w-[20rem]">
            <div className="w-full">
              {/* Month navigation */}
              <div className="mb-5 flex w-full items-center justify-between px-4">
                <Button
                  variant="inverted"
                  className="border-none"
                  isInline
                  size="icon"
                  iconLeft={<ChevronLeft className="h-6 w-6" />}
                  onClick={handlePrevMonth}
                />
                <span className="typography-product-subheading mx-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  {format(currentDate, 'MMMM yyyy')}
                </span>
                <Button
                  variant="inverted"
                  className="border-none"
                  isInline
                  size="icon"
                  iconLeft={<ChevronRight className="h-6 w-6" />}
                  onClick={handleNextMonth}
                />
              </div>

              {/* Weekday headers */}
              <div className="grid w-full grid-cols-7 gap-1">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                  <div
                    key={day}
                    className="py-2 text-center text-sm font-medium text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
                  >
                    {day}
                  </div>
                ))}

                {/* Calendar days */}
                {days.map((day) => {
                  const isSelectable = isDaySelectable(day);
                  const isCurrentMonth = format(day, 'MM') === format(currentDate, 'MM');

                  return (
                    <div
                      key={day.toString()}
                      className={`relative col-auto
                                ${getDay(day) === 0 ? 'col-start-1' : ''}
                                before:block
                                before:pb-[100%]
                                before:content-['']`}
                    >
                      <button
                        type="button"
                        onClick={() => handleDateSelect(day, close)}
                        disabled={!isSelectable}
                        className={classNames(
                          `absolute bottom-0 left-0 right-0 top-0 flex w-full items-center justify-center
                          rounded-full text-sm`,
                          !isCurrentMonth
                            ? 'text-color-text-lightmode-disabled dark:text-color-text-darkmode-disabled'
                            : 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
                          selectedDate !== null && isSameDay(day, selectedDate)
                            ? '!bg-color-bg-lightmode-brand !text-color-text-lightmode-invert !ring-0 dark:!text-color-text-darkmode-invert'
                            : '',
                          isToday(day) ? 'ring-1 ring-black dark:ring-white' : '',
                          !isSelectable
                            ? 'cursor-not-allowed opacity-50'
                            : 'hover:bg-color-bg-lightmode-secondary dark:hover:bg-color-bg-darkmode-secondary',
                        )}
                      >
                        {format(day, 'd')}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </Popover.Panel>
        </>
      )}
    </Popover>
  );
};

export default ClinicDatePicker;
